import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api, ErreurApi,
  type ChampActe, type FormulaireActe, type SectionActe,
} from './api';
import { base64De, empreinteLocale, octets } from './fichier';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE FORMULAIRE DE L'ACTE — ce que l'autorité réceptrice complète, et verse
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CE QUI A DÉCIDÉ CET ÉCRAN, mesuré le 22/09/2026 sur l'acte de dévolution
 * successorale `GN-DOC-000000138` : **72 mentions imprimées « À COMPLÉTER »,
 * et aucun écran pour les fournir**. L'acte disait honnêtement ce que le
 * registre ne détient pas — à un notaire qui ne pouvait rien y faire.
 *
 * ⚠️⚠️ CET ÉCRAN NE CONNAÎT NI LA DONATION, NI LA SUCCESSION, NI LE NOM D'UNE
 * SEULE PARTIE. Il reçoit du service une définition dérivée de
 * `doc.acte_autorite.composition` — la même source que l'acte imprimé — et
 * sait dessiner huit types de champ. Écrire ici les cadres à la main aurait
 * produit un formulaire qui diverge de l'acte au premier type ajouté, et c'est
 * toujours celui qu'on oublie qui reçoit la saisie de l'usager. Ajouter un
 * acte reste un INSERT, sans une ligne d'interface.
 *
 * ⚠️ LES CHAMPS DU GÉOMÈTRE NE SONT PAS ICI, ET CE N'EST PAS UN OUBLI. La
 * précision du levé, la longueur de façade, les bornes relèvent d'un bornage
 * contradictoire : une case « longueur de façade » donnerait à un notaire le
 * moyen d'imprimer une mesure de terrain que personne n'a faite. Le service
 * ne les produit pas ; `tests/complement-acte.test.ts` le vérifie dans les
 * deux sens.
 */

/** Ce que l'agent a tapé : section → rang du cadre → clé du champ → valeur. */
type Saisies = Record<string, Record<number, Record<string, unknown>>>;

/** Combien de cadres cette section porte, cadres ajoutés compris. */
function nbBlocs(section: SectionActe, ajouts: Record<string, number>): number {
  return section.blocs.length + (ajouts[section.cle] ?? 0);
}

/**
 * UNE SECTION EST-ELLE UN TABLEAU ?
 *
 * ⚠️ LE DISCRIMINANT EST LE CHEMIN DU PREMIER CADRE, PAS LE NOMBRE DE CADRES.
 * Le service sert `temoins.0` dès le premier témoin (un témoin est toujours
 * l'un d'une liste) et `defunt` tout court pour une partie unique. Compter les
 * cadres ferait basculer une liste d'un seul élément en objet, et la fusion
 * côté service remplacerait alors le tableau du dépôt par un objet.
 */
function estTableau(section: SectionActe, ajouts: Record<string, number>): boolean {
  if (!section.repetable) return false;
  return section.blocs[0]!.chemin !== section.cle || nbBlocs(section, ajouts) > 1;
}

/** Pose une valeur à un chemin pointé, en créant les nichoirs manquants. */
function poser(cible: Record<string, unknown>, chemin: string, valeur: unknown): void {
  const morceaux = chemin.split('.');
  let niveau: any = cible;
  for (let i = 0; i < morceaux.length - 1; i += 1) {
    niveau[morceaux[i]!] ??= {};
    niveau = niveau[morceaux[i]!];
  }
  niveau[morceaux[morceaux.length - 1]!] = valeur;
}

/**
 * LE COMPLÉMENT À ENVOYER — et ce qu'il embarque, champ par champ.
 *
 * ⚠️⚠️ TROIS RAISONS D'INCLURE UN CHAMP, ET LA TROISIÈME EST UN CORRECTIF DE
 * FOND. (1) l'agent vient de le saisir ; (2) il venait déjà du complément, et
 * l'omettre l'effacerait, puisque chaque version REMPLACE la précédente ;
 * (3) la section est un TABLEAU et le champ a une valeur — parce qu'un tableau
 * se fusionne en bloc côté service : n'envoyer que les cadres modifiés ferait
 * disparaître les témoins déjà déclarés au guichet.
 *
 * ⚠️ RENVOYER UNE VALEUR DU DÉPÔT NE S'ATTRIBUE RIEN : le service décide la
 * source par COMPARAISON, pas par présence. Une valeur identique reste créditée
 * au guichet.
 */
function construireComplement(
  sections: SectionActe[], saisies: Saisies, ajouts: Record<string, number>,
): Record<string, unknown> {
  const sortie: Record<string, unknown> = {};

  for (const section of sections) {
    const tableau = estTableau(section, ajouts);
    const total = nbBlocs(section, ajouts);
    const cadres: Record<string, unknown>[] = [];

    for (let i = 0; i < total; i += 1) {
      const bloc = section.blocs[i];
      const champs = bloc?.champs ?? gabaritEnChamps(section);
      const saisieDuCadre = saisies[section.cle]?.[i] ?? {};
      const valeurs: Record<string, unknown> = {};
      for (const c of champs) {
        const saisi = Object.prototype.hasOwnProperty.call(saisieDuCadre, c.cle);
        const garder = saisi || c.source === 'complement'
          || (tableau && c.valeur !== undefined && c.valeur !== null);
        if (!garder) continue;
        const v = saisi ? saisieDuCadre[c.cle] : c.valeur;
        // ⚠️ `null` EFFACE, et c'est voulu : une saisie erronée doit pouvoir
        //    redevenir « non renseignée ». Une chaîne vide n'est pas une valeur.
        poser(valeurs, c.cle, v === '' ? null : v);
      }
      cadres.push(valeurs);
    }

    const nonVides = cadres.filter((c) => Object.keys(c).length > 0);
    if (nonVides.length === 0) continue;

    // ⚠️ LES STIPULATIONS VIVENT À LA RACINE : leur clé EST celle de la clause.
    if (section.cle === 'clauses') {
      Object.assign(sortie, cadres[0]);
      continue;
    }
    sortie[section.cle] = tableau ? cadres : cadres[0];
  }
  return sortie;
}

/** Les champs d'un cadre ajouté : le gabarit de la section, sans valeur. */
function gabaritEnChamps(section: SectionActe): ChampActe[] {
  return (section.gabarit ?? []).map((c) => ({ ...c, chemin: '' } as ChampActe));
}

// ═══════════════════════════════════════════════════════════════════════════
// LE RENDU D'UN CHAMP — huit types, et rien d'autre
// ═══════════════════════════════════════════════════════════════════════════

function Champ({ champ, valeur, onChange }: {
  champ: ChampActe; valeur: unknown; onChange: (v: unknown) => void;
}) {
  const vide = valeur === undefined || valeur === null || valeur === '';
  const texte = vide ? '' : String(valeur);

  const commun = {
    id: `c-${champ.cle}`,
    'aria-describedby': champ.aide ? `a-${champ.cle}` : undefined,
  };

  return (
    <label className={`acte-champ${vide ? ' acte-manque' : ''}`}>
      <span className="acte-champ-titre">
        {champ.libelle}
        {/* ⚠️ LA PROVENANCE EST VISIBLE À LA SAISIE, pas seulement à l'impression :
            l'agent doit savoir s'il corrige la déclaration d'un usager ou s'il
            comble un vide. Les deux gestes n'engagent pas la même chose. */}
        {/* ⚠️ UNIFORMISATION (mandat du 16/10) : l'ordre d'autorité se LIT —
            Mowibo ID versé > constaté (jamais donné pour vérité) > guichet >
            vous > à compléter. Mêmes mots partout dans le poste. */}
        {champ.source === 'identite' && <span className="acte-source acte-source-depot">Mowibo ID — versé</span>}
        {champ.source === 'constat' && <span className="acte-source acte-source-manque">constaté — non vérifié</span>}
        {champ.source === 'depot' && <span className="acte-source acte-source-depot">guichet</span>}
        {champ.source === 'complement' && <span className="acte-source acte-source-vous">vous</span>}
        {vide && <span className="acte-source acte-source-manque">à compléter</span>}
      </span>

      {champ.type === 'liste' ? (
        <select {...commun} value={texte} onChange={(e) => onChange(e.target.value || null)}>
          {/* ⚠️ « NON RENSEIGNÉ » EST UNE VALEUR LICITE, et elle est la
              première : sans elle, ouvrir l'écran choisirait la première
              option pour l'agent et l'acte imprimerait un choix que personne
              n'a fait. */}
          <option value="">— non renseigné —</option>
          {(champ.options ?? []).map((o) => (
            <option key={o.valeur} value={o.valeur}>{o.libelle}</option>
          ))}
        </select>
      ) : champ.type === 'booleen' ? (
        /* ⚠️ TROIS ÉTATS, JAMAIS UNE CASE À COCHER. « On ne sait pas » n'est
           pas « non » : sur le consentement du conjoint, l'un veut dire
           « non recueilli » et l'autre « REFUSÉ ». Une case les confondrait,
           et un refus passerait pour un oubli. */
        <select {...commun} value={valeur === true ? 'true' : valeur === false ? 'false' : ''}
                onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}>
          <option value="">— non renseigné —</option>
          <option value="true">Oui</option>
          <option value="false">Non</option>
        </select>
      ) : champ.type === 'texte_long' ? (
        <textarea {...commun} rows={3} maxLength={champ.max} value={texte}
                  onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input
          {...commun}
          type={champ.type === 'date' ? 'date' : champ.type === 'nombre' ? 'number'
            : champ.type === 'courriel' ? 'email' : champ.type === 'telephone' ? 'tel' : 'text'}
          maxLength={champ.type === 'nombre' || champ.type === 'date' ? undefined : champ.max}
          value={texte}
          onChange={(e) => onChange(
            champ.type === 'nombre'
              ? (e.target.value === '' ? null : Number(e.target.value))
              : e.target.value)}
        />
      )}
      {champ.aide && <span className="acte-aide" id={`a-${champ.cle}`}>{champ.aide}</span>}
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// L'ÉCRAN
// ═══════════════════════════════════════════════════════════════════════════

export function FormulaireDeLActe({ reference }: { reference: string }) {
  const [f, setF] = useState<FormulaireActe | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saisies, setSaisies] = useState<Saisies>({});
  const [ajouts, setAjouts] = useState<Record<string, number>>({});
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setF(await api.affaires.formulaire(reference));
      setSaisies({}); setAjouts({});
    } catch (e) { setErreur(String((e as Error).message ?? e)); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger]);

  const poserSaisie = (section: string, rang: number, cle: string, v: unknown) =>
    setSaisies((x) => ({ ...x, [section]: { ...x[section], [rang]: { ...x[section]?.[rang], [cle]: v } } }));

  /** Ce qui reste en surbrillance sur l'acte, saisies en cours comprises. */
  const restant = useMemo(() => {
    if (!f) return 0;
    let n = 0;
    for (const s of f.sections) {
      for (let i = 0; i < nbBlocs(s, ajouts); i += 1) {
        const champs = s.blocs[i]?.champs ?? gabaritEnChamps(s);
        for (const c of champs) {
          const saisi = saisies[s.cle]?.[i];
          const v = saisi && Object.prototype.hasOwnProperty.call(saisi, c.cle) ? saisi[c.cle] : c.valeur;
          if (v === undefined || v === null || v === '') n += 1;
        }
      }
    }
    return n;
  }, [f, saisies, ajouts]);

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault();
    if (!f) return;
    setEnvoi(true); setErreur(null); setMessage(null);
    try {
      const r = await api.affaires.enregistrerFormulaire(reference, {
        contenu: construireComplement(f.sections, saisies, ajouts),
        motif, versionAttendue: f.version,
      });
      setMessage(r.message); setMotif('');
      await charger();
    } catch (err) {
      const e2 = err as ErreurApi;
      setErreur(`${e2.message}${e2.details ? ` — ${JSON.stringify(e2.details)}` : ''}`);
    } finally { setEnvoi(false); }
  }

  if (erreur && !f) return <p className="erreur" role="alert">{erreur}</p>;
  if (!f) return <p className="attente">Lecture du formulaire…</p>;
  const peutTraiterAffaire = f.peutTraiterAffaire === true;

  /* ⚠️⚠️ IL N'Y A PLUS DE RETOUR ANTICIPÉ ICI, ET C'EST LE CORRECTIF.
     Ce composant rendait `if (!f.acte) return <la note/>` — et le versement de
     pièces, monté quarante lignes plus bas, MOURAIT AVEC L'ACTE. Mesuré le
     22/09/2026 : le service acceptait déjà `POST /pieces/televerser` sur
     n'importe quelle affaire (il n'exige qu'un droit, jamais un acte), et le
     versement n'était atteignable que sur **11 affaires sur 48**. Sur les 37
     autres, l'agent voyait une note et AUCUN geste — le bloc des pièces du
     parcours se masque lui aussi quand la liste est vide, donc l'affaire était
     muette sur ses documents.

     ⚠️ « PAS D'ACTE » N'EST TOUJOURS PAS « RIEN À REMPLIR » : le service dit
     POURQUOI, on le montre. Ce qui change, c'est que l'absence d'acte ne
     retire plus un geste qui ne dépendait pas de lui.

     ⚠️ UN `return` ANTICIPÉ N'EST PAS UN RANGEMENT : c'est une clause qui
     emporte tout ce qui suit, y compris ce qui a été ajouté après lui par
     quelqu'un d'autre. `tests/versement-sur-toute-affaire.test.ts` refuse
     qu'il revienne. */
  return (
    <section className="acte-formulaire">
      {!f.acte && <div className="note">{f.pourquoi}</div>}

      {/* ⚠️⚠️ HORS DU BLOC DE L'ACTE, ET C'EST UN CORRECTIF. Ces deux lignes
          vivaient DEDANS : sur une affaire sans acte, un versement réussi
          appelait `setMessage` et **la confirmation ne s'affichait nulle part**.
          Le service rendait 201, la pièce entrait au coffre, l'écran ne disait
          rien — l'agent verse une seconde fois, puis conclut que le bouton ne
          marche pas. C'est la forme EXACTE du faux succès que la session
          précédente venait de corriger sur la liste des pièces, réintroduite à
          dix lignes de là par un bloc conditionnel.
          ⚠️ Trouvé en relisant l'arbre de rendu, pas par un essai : le message
          existe, le composant se rend, et rien ne lève. */}
      {/* ⚠️ L'AVERTISSEMENT DE CLÔTURE VAUT AVEC OU SANS ACTE, et le service
          en compose la phrase juste pour chaque cas. Le laisser dans le bloc
          de l'acte taisait la clôture à qui verse une pièce — sur 6 des 7
          affaires d'extrait cadastral, qui sont closes. */}
      {f.avertissement && <div className="note">{f.avertissement}</div>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {message && <p className="succes" role="status">{message}</p>}

      {f.acte && (<>
      <header className="acte-entete">
        <div>
          <h4>{f.acte!.libelle}</h4>
          <p className="mention">
            {f.depot
              ? <>État civil recueilli au guichet sous <strong>{f.depot.reference}</strong>.</>
              : <>Aucun formulaire de dépôt : tout ce qui suit est à votre charge.</>}
            {f.complement && <> Dernier complément : version {f.complement.version}, {' '}
              {f.complement.saisiPar ?? 'auteur inconnu'}, le{' '}
              {new Date(f.complement.saisiLe).toLocaleString('fr-FR')}.</>}
          </p>
        </div>
        {/* ⚠️ DEUX COMPTES, JAMAIS UN TOTAL : « 72 mentions » ne se décide pas,
            « 58 à compléter dont 14 déjà fournies par vous » se décide. */}
        <ul className="acte-compteurs">
          <li><strong>{restant}</strong> à compléter</li>
          <li><strong>{f.etablies ?? 0}</strong> établies par Mowibo ID</li>
          <li><strong>{f.complete}</strong> fournies par l'autorité</li>
          <li><strong>{f.total}</strong> mentions au total</li>
        </ul>
      </header>


      <form onSubmit={enregistrer}>
        {f.sections.map((s) => (
          <fieldset key={s.cle} className="acte-section">
            <legend>{s.titre}</legend>
            {s.aide && <p className="acte-aide">{s.aide}</p>}

            {Array.from({ length: nbBlocs(s, ajouts) }, (_, i) => {
              const bloc = s.blocs[i];
              const champs = bloc?.champs ?? gabaritEnChamps(s);
              return (
                <div key={i} className="acte-bloc">
                  {nbBlocs(s, ajouts) > 1 && (
                    <h5>{bloc?.titre ?? `${s.titre} — ${i + 1}`}</h5>
                  )}
                  <div className="acte-grille">
                    {champs.map((c) => {
                      const saisi = saisies[s.cle]?.[i];
                      const v = saisi && Object.prototype.hasOwnProperty.call(saisi, c.cle)
                        ? saisi[c.cle] : c.valeur;
                      return (
                        <Champ key={c.cle} champ={c} valeur={v}
                               onChange={(x) => poserSaisie(s.cle, i, c.cle, x)} />
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {s.repetable && peutTraiterAffaire && (
              <button type="button" className="bouton discret"
                      onClick={() => setAjouts((x) => ({ ...x, [s.cle]: (x[s.cle] ?? 0) + 1 }))}>
                + Ajouter {s.titre.toLowerCase()}
              </button>
            )}
          </fieldset>
        ))}

        {peutTraiterAffaire && (
          <div className="acte-barre">
            <label className="acte-champ">
              <span className="acte-champ-titre">Motif de la saisie</span>
              <input value={motif} onChange={(e) => setMotif(e.target.value)}
                     required minLength={3} maxLength={500}
                     placeholder="Pièces d'état civil produites par les parties…" />
              <span className="acte-aide">
                Consigné au journal avec votre nom. Chaque enregistrement crée une version :
                la précédente n'est pas effacée — elle explique les actes déjà imprimés.
              </span>
            </label>
            <button className="bouton primaire" disabled={envoi}>
              {envoi ? 'Enregistrement…' : `Enregistrer la version ${f.version + 1}`}
            </button>
          </div>
        )}
      </form>
      </>)}

      {/* ⚠️ HORS DE TOUTE CONDITION SUR L'ACTE. Le seul juge est le droit, et
          c'est le service qui le dit. Une affaire sans acte a ses pièces :
          une demande d'extrait cadastral exige une identité et un mandat. */}
      {peutTraiterAffaire && <VersementDePiece reference={reference} onVerse={setMessage} />}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LE VERSEMENT D'UNE PIÈCE — le fichier ET la pièce, en un geste
// ═══════════════════════════════════════════════════════════════════════════

function VersementDePiece({ reference, onVerse }: {
  reference: string; onVerse: (m: string) => void;
}) {
  const [types, setTypes] = useState<{ code: string; libelle: string; aide: string | null }[]>([]);
  const [code, setCode] = useState('');
  const [emetteur, setEmetteur] = useState('');
  const [dateDocument, setDateDocument] = useState(new Date().toISOString().slice(0, 10));
  const [fichier, setFichier] = useState<File | null>(null);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    api.affaires.typesDePiece(reference)
      .then((r) => { setTypes(r.types); setCode((c) => c || (r.types[0]?.code ?? '')); })
      .catch((e) => setErreur(String((e as Error).message ?? e)));
  }, [reference]);

  const aide = types.find((t) => t.code === code)?.aide;

  async function verser(e: React.FormEvent) {
    e.preventDefault();
    if (!fichier) { setErreur('Aucun fichier choisi.'); return; }
    setEnvoi(true); setErreur(null);
    try {
      const brut = await fichier.arrayBuffer();
      const r = await api.affaires.televerserPiece(reference, {
        codePiece: code, emetteur, dateDocument,
        typeMime: fichier.type || 'application/octet-stream',
        contenuBase64: base64De(brut),
        empreinteAnnoncee: await empreinteLocale(brut),
        motif,
      });
      onVerse(r.message);
      setFichier(null); setMotif('');
    } catch (err) {
      const e2 = err as ErreurApi;
      setErreur(`${e2.message}${e2.details ? ` — ${JSON.stringify(e2.details)}` : ''}`);
    } finally { setEnvoi(false); }
  }

  return (
    <form className="acte-versement" onSubmit={verser}>
      <h4>Verser une pièce à l'affaire</h4>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="acte-grille">
        <label className="acte-champ">
          <span className="acte-champ-titre">Nature de la pièce</span>
          <select value={code} onChange={(e) => setCode(e.target.value)} required>
            {types.map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
          </select>
          {aide && <span className="acte-aide">{aide}</span>}
        </label>
        <label className="acte-champ">
          <span className="acte-champ-titre">Émetteur</span>
          <input value={emetteur} onChange={(e) => setEmetteur(e.target.value)} required
                 minLength={2} maxLength={200} placeholder="Étude notariale, mairie, tribunal…" />
        </label>
        <label className="acte-champ">
          <span className="acte-champ-titre">Date du document</span>
          <input type="date" value={dateDocument} onChange={(e) => setDateDocument(e.target.value)} required />
        </label>
        <label className="acte-champ">
          <span className="acte-champ-titre">Fichier</span>
          <input type="file" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} required
                 accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp,.docx,.xlsx,.txt,.csv,.json,.zip" />
          {fichier && (
            <span className="acte-aide">
              {fichier.name} — {octets(fichier.size)} — {fichier.type || 'type inconnu'}
            </span>
          )}
        </label>
        <label className="acte-champ">
          <span className="acte-champ-titre">Motif du versement</span>
          <input value={motif} onChange={(e) => setMotif(e.target.value)} required
                 minLength={3} maxLength={500} />
        </label>
      </div>
      <p className="mention">
        Le fichier est chiffré au repos et son empreinte est calculée sur ce poste <em>et</em> par
        le serveur, puis comparée — un fichier altéré pendant le transfert est refusé. La pièce
        entre au statut <strong>« déposée »</strong> : celui qui verse ne s'accepte pas lui-même,
        l'acceptation revient à l'instruction.
      </p>
      <div className="actions">
        <button className="bouton primaire" disabled={envoi}>
          {envoi ? 'Chiffrement et versement…' : "Verser à l'affaire"}
        </button>
      </div>
    </form>
  );
}
